// src/components/cabling/CablingPriorityList.jsx
import React, { useState } from 'react';
import { COLORS } from '../../constants';

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const CriticalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const HighIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const MediumIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export default function CablingPriorityList({ copperData, fibreData }) {
  const [filter, setFilter] = useState('all');
  
  const allProjects = [
    ...(copperData || []).map(c => ({ ...c, type: 'Copper', progress: c.progress || 0, remaining: c.remaining || 0 })),
    ...(fibreData || []).map(f => ({ ...f, type: 'Fibre', progress: f.progress || 0, remaining: f.remaining || 0 })),
  ];
  
  const priorityProjects = allProjects
    .filter(p => p.progress < 100 && p.remaining > 0)
    .sort((a, b) => a.progress - b.progress);
  
  const getPriorityLevel = (progress) => {
    if (progress < 30) return { label: 'Critical', color: COLORS.danger, icon: <CriticalIcon /> };
    if (progress < 60) return { label: 'High', color: COLORS.warning, icon: <HighIcon /> };
    return { label: 'Medium', color: COLORS.info, icon: <MediumIcon /> };
  };
  
  const priorityFilters = ['all', 'critical', 'high', 'medium'];
  
  const filteredProjects = priorityProjects.filter(p => {
    if (filter === 'all') return true;
    const priority = getPriorityLevel(p.progress);
    return priority.label.toLowerCase() === filter;
  }).slice(0, 5);
  
  if (priorityProjects.length === 0) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 24,
          border: `1px solid ${COLORS.line}`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 12, color: COLORS.success }}>✓</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
          All Caught Up!
        </div>
        <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
          No pending high-priority items
        </div>
      </div>
    );
  }
  
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        padding: 24,
        border: `1px solid ${COLORS.line}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <AlertIcon />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>Priority Actions</h3>
        <span style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 8px', background: COLORS.dangerLight, color: COLORS.danger, borderRadius: 12, fontWeight: 600 }}>
          {priorityProjects.length} items
        </span>
      </div>
      
      {/* Filter Pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {priorityFilters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 20,
              border: `1px solid ${filter === f ? COLORS.primary : COLORS.line}`,
              background: filter === f ? COLORS.primary : 'white',
              color: filter === f ? 'white' : COLORS.textSecondary,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Priority List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {filteredProjects.map((project, idx) => {
          const priority = getPriorityLevel(project.progress);
          return (
            <div
              key={idx}
              style={{
                padding: 12,
                background: COLORS.surface,
                borderRadius: 8,
                borderLeft: `3px solid ${priority.color}`,
                transition: 'transform 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{ color: priority.color }}>{priority.icon}</div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                      {project.productCode || project.bundle || `Project ${idx + 1}`}
                    </span>
                    <span style={{ fontSize: 10, padding: '2px 6px', background: priority.color + '20', color: priority.color, borderRadius: 8, fontWeight: 600 }}>
                      {priority.label}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
                    {project.type} · {project.remaining} units remaining
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: priority.color }}>
                    {Math.round(project.progress)}%
                  </div>
                </div>
              </div>
              
              <div
                style={{
                  height: 6,
                  background: COLORS.line,
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${project.progress}%`,
                    height: '100%',
                    background: priority.color,
                    borderRadius: 3,
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Summary line */}
      {filter !== 'all' && filteredProjects.length === 0 && (
        <div style={{ textAlign: 'center', padding: 20, color: COLORS.textSecondary, fontSize: 12 }}>
          No {filter} priority items
        </div>
      )}
    </div>
  );
}